import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, Search, Settings as SettingsIcon, ChevronDown, Sparkles, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import SearchAutocomplete from "@/components/search/SearchAutocomplete";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LINKS = [
  { to: "/explore", label: "Explore" },
  { to: "/journey", label: "My Journey", auth: true },
  { to: "/curate", label: "Curate", roles: ["curator", "admin"] },
];

const SiteNav = () => {
  const { user, logout, openAuth } = useAuth();
  const navigate = useNavigate();

  const visibleLinks = LINKS.filter((l) => {
    if (l.roles) return user && l.roles.includes(user.role);
    return true;
  });

  const handleNav = (l, e) => {
    if (l.auth && !user) {
      e.preventDefault();
      openAuth();
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-[#fdfdfb]/85 backdrop-blur-md border-b border-[#14140f]/10">
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2.5 group"
          data-testid="brand-home-btn"
          aria-label="Living Wisdom Explorer — home"
        >
          <span className="w-9 h-9 rounded-md bg-[#ff7360] flex items-center justify-center text-[#14140f] font-serif text-xl transition-transform duration-200 group-hover:scale-105">
            {"\u0950"}
          </span>
          <div className="leading-tight text-left">
            <div className="font-serif text-lg tracking-tight text-[#14140f]">
              Living Wisdom <span className="text-[#a86400]">Explorer</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#14140f]/45 -mt-0.5">
              Ancient Indian Philosophy
            </div>
          </div>
        </button>

        {/* Persistent "Ask" search — always reachable from any page (desktop) */}
        <div className="hidden lg:block flex-1 max-w-sm mx-6" data-testid="nav-ask-search">
          <SearchAutocomplete
            testIdPrefix="nav-ask"
            placeholder="Ask anything…"
            icon={<Search className="w-4 h-4 text-[#14140f]/35 shrink-0" />}
            submitIcon={<Search className="w-3.5 h-3.5" />}
            pillClassName="flex items-center gap-2 w-full bg-white/70 rounded-full border border-[#14140f]/10 pl-4 pr-1.5 py-1 focus-within:border-[#ff7360] focus-within:bg-white transition-colors"
            buttonClassName="shrink-0 w-7 h-7 rounded-full bg-[#ff7360] text-[#14140f] flex items-center justify-center hover:bg-[#e85a46] transition-colors"
          />
        </div>

        <nav className="hidden md:flex items-center gap-1">
          {visibleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={(e) => handleNav(l, e)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-[#f5f4f0] text-[#14140f]"
                    : "text-[#14140f]/60 hover:text-[#14140f] hover:bg-[#f5f4f0]/60"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/answers")}
            data-testid="nav-ask-mobile-btn"
            aria-label="Ask a question"
            className="lg:hidden w-9 h-9 rounded-full flex items-center justify-center text-[#14140f]/70 hover:text-[#14140f] hover:bg-[#f5f4f0] transition-colors"
          >
            <Search className="w-5 h-5" />
          </button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-[#f5f4f0] transition-colors">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-[#14140f]/10" />
                  ) : (
                    <span className="w-8 h-8 rounded-full bg-[#ff7360] text-[#14140f] flex items-center justify-center text-sm font-semibold">
                      {user.name?.[0]?.toUpperCase()}
                    </span>
                  )}
                  <div className="hidden md:flex flex-col items-start leading-tight">
                    <span className="text-sm font-medium text-[#14140f]">{user.name}</span>
                    <span className="text-[10px] uppercase tracking-wide text-[#a86400] font-semibold">{user.role}</span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-[#14140f]/40" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 md:hidden">
                  <div className="text-sm font-medium text-[#14140f]">{user.name}</div>
                  <div className="text-[10px] uppercase tracking-wide text-[#a86400] font-semibold">{user.role}</div>
                </div>
                <DropdownMenuItem onClick={() => navigate("/ai-keys")} data-testid="user-menu-ai-keys">
                  <Sparkles className="w-4 h-4 mr-2" /> AI Keys
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="user-menu-settings">
                  <SettingsIcon className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="user-menu-admin">
                    <Users className="w-4 h-4 mr-2" /> User Management
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="user-menu-logout" className="text-red-600 focus:text-red-700">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={openAuth}
              data-testid="login-btn"
              className="bg-[#ff7360] hover:bg-[#e85a46] text-[#14140f] rounded-full px-5"
            >
              Sign in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default SiteNav;
